import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Link as MuiLink,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import FormSection from '../../components/StepperForm/FormSection.jsx';
import { formatFileSize, getFileTypeCategory } from '../../utils/documents';
import { getRecordingDisplayName, getRecordingUrl } from './callRecordHelpers';
import { ACCEPT_AUDIO, isAudioFile } from './recordingFileTypes';

/**
 * @typedef {{ key: string, file: File, attachmentId: string | null, uploading: boolean }} RecordingUploadRow
 */

/**
 * File is chosen here; parent uploads to /attachments immediately and passes back attachment id(s).
 * saving = POST/PUT call-records in progress.
 *
 * **Single file** (default): `file`, `attachmentId`, `onFileChange(file)`
 * **Multiple** (create): `multiple`, `items`, `onFilesSelected(File[])`, `onRemoveItem(key)`
 */
const CallRecordingUploadSection = ({
  multiple = false,
  /** @type {RecordingUploadRow[]} */
  items = [],
  onFilesSelected,
  onRemoveItem,
  file,
  attachmentId = null,
  /** Current recordings from API (edit flow) — shown until user picks a replacement file */
  existingRecordings = [],
  uploadingAttachment = false,
  saving = false,
  onFileChange,
  onRemove,
  disabled = false,
  /** @deprecated use saving */
  submitting = false,
  optionalReplace = false,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const isSaving = saving || submitting;
  const anyMultiUploading = multiple && items.some((r) => r.uploading);
  const busy = disabled || isSaving || uploadingAttachment || anyMultiUploading;
  const existingList = Array.isArray(existingRecordings) ? existingRecordings : [];

  const handleFiles = (files) => {
    const list = Array.from(files || []).filter(isAudioFile);
    if (!list.length) {
      toast.warn('Please choose audio files (e.g. MP3, WAV, M4A).');
      return;
    }
    if (multiple) {
      onFilesSelected?.(list);
      return;
    }
    const next = list[0];
    if (list.length > 1) {
      toast.info('Only one file is used here — the first file was selected.');
    }
    onFileChange?.(next);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (busy) return;
    handleFiles(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!busy) setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const chipLabelForType = (mime) => {
    if (mime?.startsWith?.('audio/')) return 'audio';
    if (!mime) return 'audio';
    return getFileTypeCategory(mime);
  };

  const multiTitle = () => {
    if (anyMultiUploading) {
      const n = items.filter((r) => r.uploading).length;
      return `Uploading ${n} file(s)…`;
    }
    if (items.length) return `${items.length} file(s) selected`;
    return 'No recordings selected';
  };

  return (
    <FormSection title={multiple ? 'Recording files' : 'Recording file'} bgColor="blue.50">
      <Alert severity="info" sx={{ mb: 2 }}>
        {optionalReplace
          ? 'Optional: pick a new file — it uploads to the attachment API right away. Click Save to update the call record.'
          : multiple
            ? 'Select one or more audio files — each uploads to the attachment API. Click Save to add the call record with all attachment ids.'
            : 'Pick a file to upload it to the attachment API immediately. Click Save to add the call record with that attachment id.'}
      </Alert>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_AUDIO}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <Card
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'grey.300',
          bgcolor: dragOver ? 'primary.50' : 'grey.50',
          cursor: busy ? 'not-allowed' : 'pointer',
          mb: 3,
          opacity: busy ? 0.65 : 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: busy ? 'grey.300' : 'primary.main',
            bgcolor: busy ? 'grey.50' : 'primary.50',
          },
        }}
        onClick={() => {
          if (!busy) inputRef.current?.click();
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {multiple
              ? multiTitle()
              : uploadingAttachment
                ? 'Uploading attachment…'
                : isSaving
                  ? 'Saving recording…'
                  : file
                    ? file.name
                    : optionalReplace && existingList.length
                      ? `Current: ${existingList.length} recording(s)`
                      : 'No recording selected'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {multiple
              ? anyMultiUploading
                ? 'Please wait'
                : 'Click to browse or drag one or more audio files here'
              : uploadingAttachment
                ? 'Please wait'
                : isSaving
                  ? 'Adding call record'
                  : optionalReplace && existingList.length && !file
                    ? 'Click below to replace or drag a new audio file here'
                    : 'Click to browse or drag an audio file here'}
          </Typography>
        </CardContent>
      </Card>

      {(uploadingAttachment || isSaving || anyMultiUploading) && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>File name</TableCell>
              <TableCell>Attachment id</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {multiple ? (
              items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No files yet — add audio files above
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.file.name}</TableCell>
                    <TableCell>
                      {row.uploading ? (
                        <Typography variant="caption" color="text.secondary">
                          …
                        </Typography>
                      ) : row.attachmentId ? (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          noWrap
                          title={row.attachmentId}
                        >
                          {row.attachmentId}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="error">
                          Upload failed
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatFileSize(row.file.size)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={chipLabelForType(row.file.type)} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveItem?.(row.key);
                        }}
                        title="Remove"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )
            ) : !file && optionalReplace && existingList.length ? (
              existingList.map((rec, idx) => {
                const label = getRecordingDisplayName(rec);
                const recId = rec?.id ?? rec?.uuid ?? `rec-${idx}`;
                return (
                  <TableRow key={recId}>
                    <TableCell>{label}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} noWrap title={recId}>
                        {recId}
                      </Typography>
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          rec?.type?.startsWith?.('audio/')
                            ? 'audio'
                            : rec?.type
                              ? getFileTypeCategory(rec.type)
                              : 'audio'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        Current
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : !file ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {optionalReplace
                      ? 'No replacement file — existing recording will be kept'
                      : 'No file selected yet'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell>{file.name}</TableCell>
                <TableCell>
                  {uploadingAttachment ? (
                    <Typography variant="caption" color="text.secondary">
                      …
                    </Typography>
                  ) : attachmentId ? (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} noWrap title={attachmentId}>
                      {attachmentId}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="error">
                      Upload failed
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatFileSize(file.size)}</TableCell>
                <TableCell>
                  <Chip size="small" label={file.type ? getFileTypeCategory(file.type) : 'audio'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="error"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.();
                    }}
                    title="Remove"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {!multiple && file && attachmentId && !uploadingAttachment && !isSaving && (
          <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="caption" color="text.secondary">
              {optionalReplace
                ? 'New attachment ready — click Save to update the call record'
                : 'Attachment stored — click Save to add the call record'}
            </Typography>
          </Box>
        )}
        {multiple &&
          items.length > 0 &&
          items.every((r) => r.attachmentId && !r.uploading) &&
          !isSaving && (
            <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                {items.length === 1
                  ? 'Attachment ready — click Save to add the call record'
                  : `${items.length} attachments ready — click Save to add the call record`}
              </Typography>
            </Box>
          )}
        {!file && optionalReplace && existingList.length > 0 && (
          <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
            {existingList.map((rec, idx) => {
              const recUrl = getRecordingUrl(rec);
              if (!recUrl) return null;
              const label = getRecordingDisplayName(rec);
              const recId = rec?.id ?? rec?.uuid ?? `rec-audio-${idx}`;
              return (
                <Box key={recId} sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {existingList.length > 1 ? `${idx + 1}. ${label}` : label}
                  </Typography>
                  <audio controls preload="metadata" src={recUrl} style={{ width: '100%' }}>
                    Your browser does not support audio.
                  </audio>
                  <MuiLink
                    href={recUrl}
                    download={label}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                    Download
                  </MuiLink>
                </Box>
              );
            })}
          </Box>
        )}
      </TableContainer>
    </FormSection>
  );
};

export default CallRecordingUploadSection;
